import React from 'react';
import type {HedgePosition} from '../../../types/hedge';
import {fmtPx, LEG_EXIT_REASON_TEXT} from './hedgeDisplay';

type Market = 'SPOT' | 'SWAP';

/** 触发价进度（%）= (现价 − 成本价) / (触发价 − 成本价)：0=在成本价、100=到达触发价、负=尚未朝该方向推进 */
interface Progress {
    label: string;
    pct?: number;
    crossed: boolean;
    cls: 'ok' | 'warn';
}

interface Endpoint {
    px: number;
    label: string;
}

interface LegView {
    market: Market;
    title: string;
    /** 保护单未挂载/待补挂（价格仍为研报计划价） */
    unmounted: boolean;
    lastPx?: number;
    /** 成本价（盈亏平衡价）；后端不可算时回退成交均价 */
    costPx?: number;
    /** 成本价是否回退自成交均价（手续费/资金费不可得） */
    costFallback: boolean;
    /** 绘制参数；为 null 表示不可绘制（原因见 emptyText） */
    bar: null | {
        left: Endpoint;
        right: Endpoint;
        /** 成本价刻度位置（仅当严格落在两端之间） */
        costRatio?: number;
        /** 现价游标位置（0=左端 1=右端）；undefined=无现价 */
        cursorRatio?: number;
        /** 现价越过端点（游标被截断在边缘） */
        cursorOut: boolean;
    };
    progresses: Progress[];
    emptyText?: string;
}

const clamp01 = (value: number): number => Math.min(1, Math.max(0, value));

/**
 * 单腿保护价视图：条的两端取触发价（止盈/止损，仅有单边时退化为成本价）后按价格排序，
 * 使"成本价 → 触发价"的价差成为条形尺度；游标 = 现价在两端之间的相对位置，
 * 因此进度百分比与游标位置严格一致（进度=游标在"成本价 → 触发价"段内的占比）。
 */
const buildLegView = (params: {
    market: Market;
    title: string;
    avgPx?: number;
    costPx?: number;
    lastPx?: number;
    tpPx?: number;
    slPx?: number;
    unmounted: boolean;
}): LegView => {
    const {market, title, avgPx, lastPx, tpPx, slPx, unmounted} = params;
    const costFallback = null == params.costPx;
    const costPx = costFallback ? avgPx : params.costPx;
    const view: LegView = {
        market, title, unmounted, lastPx, costPx, costFallback, bar: null, progresses: [],
    };
    if (null == tpPx && null == slPx) {
        return {...view, emptyText: '研报未给出保护价'};
    }
    const shortLeg = 'SWAP' === market;
    /** 进度 = (现价 − 成本价) / (触发价 − 成本价)；成本价与触发价重合或数据缺失返回 undefined */
    const progressOf = (trigger: number): number | undefined => {
        if (null == costPx || null == lastPx) {
            return undefined;
        }
        const denominator = trigger - Number(costPx);
        if (0 === denominator) {
            return undefined;
        }
        return ((Number(lastPx) - Number(costPx)) / denominator) * 100;
    };
    // 已越过判定：现货多头涨破止盈；永续空头跌破止盈、涨破止损
    const crossedOf = (trigger: number, profitSide: boolean): boolean => {
        if (null == lastPx) {
            return false;
        }
        const last = Number(lastPx);
        if (shortLeg) {
            return profitSide ? last <= trigger : last >= trigger;
        }
        return profitSide ? last >= trigger : last <= trigger;
    };
    // 只显示价格当前所在侧的那条进度：在【成本 → 止盈】侧显示止盈，在【成本 → 止损】侧显示止损；
    // 现价或成本价不可得时无法判侧，两条都显示（不隐藏信息）
    const sideKnown = null != lastPx && null != costPx;
    const profitSide = !sideKnown || (shortLeg
        ? Number(lastPx) <= Number(costPx) : Number(lastPx) >= Number(costPx));
    const progresses: Progress[] = [];
    if (null != tpPx && (!sideKnown || null == slPx || profitSide)) {
        progresses.push({
            label: '止盈', pct: progressOf(Number(tpPx)),
            crossed: crossedOf(Number(tpPx), true), cls: 'ok',
        });
    }
    if (null != slPx && (!sideKnown || null == tpPx || !profitSide)) {
        progresses.push({
            label: '止损', pct: progressOf(Number(slPx)),
            crossed: crossedOf(Number(slPx), false), cls: 'warn',
        });
    }
    const endpoints: Endpoint[] = [];
    if (null != tpPx) {
        endpoints.push({px: Number(tpPx), label: '止盈'});
    }
    if (null != slPx) {
        endpoints.push({px: Number(slPx), label: '止损'});
    }
    if (endpoints.length < 2) {
        if (null == costPx) {
            return {...view, progresses, emptyText: '缺少成本价，无法定位'};
        }
        endpoints.push({px: Number(costPx), label: costFallback ? '均价' : '成本'});
    }
    endpoints.sort((a, b) => a.px - b.px);
    const [left, right] = endpoints;
    if (!(right.px > left.px)) {
        return {...view, progresses, emptyText: '保护价与成本价异常，无法绘制'};
    }
    const ratioOf = (px: number): number => (px - left.px) / (right.px - left.px);
    const costRatio = null == costPx ? undefined : ratioOf(Number(costPx));
    const rawCursor = null == lastPx ? undefined : ratioOf(Number(lastPx));
    return {
        ...view,
        progresses,
        bar: {
            left,
            right,
            // 成本价与端点重合（如现货行左端就是成本价）时不重复画刻度
            costRatio: null != costRatio && costRatio > 0.005 && costRatio < 0.995 ? costRatio : undefined,
            cursorRatio: null == rawCursor ? undefined : clamp01(rawCursor),
            cursorOut: null != rawCursor && (rawCursor < 0 || rawCursor > 1),
        },
    };
};

interface Props {
    position: HedgePosition;
}

/**
 * 运行中持仓的保护价进度条（主单一行、对冲单一行）。
 * 条的两端是触发价（止盈/止损，仅有单边时左端退化为成本价），游标是现价，
 * 永续行条内另标成本价刻度，区隔盈利侧与风险侧；百分比按"(现价−成本价)/(触发价−成本价)"折算。
 */
export const HedgeProtectRows: React.FC<Props> = ({position}) => {
    const p = position;
    const spotIsMain = 'SPOT' === p.mainLeg;
    const unmounted = 1 !== Number(p.protectStatus ?? 0);
    /** 单腿持有：一腿离场后剩余腿裸持（离场腿成交量已归零），由自身保护单管理 */
    const singleLeg = 'SINGLE_LEG' === p.completionState;
    const legDeparted = (market: Market): boolean =>
        singleLeg && Number(('SPOT' === market ? p.spotFilledQty : p.swapFilledQty) ?? 0) <= 0;
    const views = [
        buildLegView({
            market: 'SPOT',
            title: `${spotIsMain ? '主单' : '对冲单'} · 现货买入`,
            avgPx: p.spotAvgPx, costPx: p.spotCostPx, lastPx: p.spotLastPx,
            tpPx: p.spotTpPx, slPx: p.spotSlPx, unmounted,
        }),
        buildLegView({
            market: 'SWAP',
            title: `${spotIsMain ? '对冲单' : '主单'} · 永续空`,
            // 保护单按最新成交价触发（last），进度须与触发基准一致；缺失才回退标记价
            avgPx: p.swapAvgPx, costPx: p.swapCostPx, lastPx: p.swapTriggerPx ?? p.swapLastPx,
            tpPx: p.swapTpPx, slPx: p.swapSlPx, unmounted,
        }),
    ];

    return (
        <div className="hedge-protect-rows">
            {views.map(view => {
                const departed = legDeparted(view.market);
                const exitReason = 'SPOT' === view.market ? p.spotExitReason : p.swapExitReason;
                const exitText = null == exitReason
                    ? undefined : (LEG_EXIT_REASON_TEXT[exitReason] ?? exitReason);
                return (
                    <div className="hedge-protect-row" key={view.market}>
                        <div className="hedge-protect-head">
                            <span className="hedge-protect-title">{view.title}</span>
                            {departed && (
                                <span className="hedge-protect-flag"
                                      title={null != exitText
                                          ? `该腿已离场：${exitText}`
                                          : '该腿保护单已成交并离场'}>
                                    已离场{null != exitText ? ` · ${exitText}` : ''}
                                </span>
                            )}
                            {!departed && singleLeg && (
                                <span className="hedge-protect-flag"
                                      title="对腿已离场，本腿裸持等其自身保护单（期间不做自动干预）">裸持</span>
                            )}
                            {!departed && !singleLeg && view.unmounted && (
                                <span className="hedge-protect-flag"
                                      title="交易所保护单未挂载/待补挂，此处为研报计划价">未挂载</span>
                            )}
                        </div>
                        {departed ? (
                            <div className="hedge-protect-meta dim">
                                {null != exitText ? `该腿因${exitText}` : '该腿已离场，剩余腿继续持有等其自身保护单'}
                            </div>
                        ) : null == view.bar ? (
                            <div className="hedge-protect-meta dim">{view.emptyText}</div>
                        ) : (
                            <>
                                <div className="hedge-protect-barline"
                                     title={'条两端为触发价，游标=现价（与保护单触发基准一致：现货最新成交价 / 永续最新成交价），'
                                         + '刻度为成本价（均价 + 开仓手续费 + 已结算资金费 + 按开仓同额预估的平仓费）'}>
                                    <span className="hedge-protect-edge">
                                        {view.bar.left.label} {fmtPx(view.bar.left.px)}
                                    </span>
                                    <div className="hedge-protect-bar">
                                        {null != view.bar.costRatio && (
                                            <span className="hedge-protect-tick"
                                                  style={{left: `${view.bar.costRatio * 100}%`}}
                                                  title={`${view.costFallback ? '开仓均价' : '成本价'} ${fmtPx(view.costPx)}`}/>
                                        )}
                                        {null != view.bar.cursorRatio && (
                                            <span className={`hedge-protect-cursor${view.bar.cursorOut ? ' out' : ''}`}
                                                  style={{left: `${view.bar.cursorRatio * 100}%`}}
                                                  title={`现价 ${fmtPx(view.lastPx)}`}/>
                                        )}
                                    </div>
                                    <span className="hedge-protect-edge">
                                        {view.bar.right.label} {fmtPx(view.bar.right.px)}
                                    </span>
                                </div>
                                <div className="hedge-protect-meta">
                                    <span className="dim"
                                          title={'SPOT' === view.market
                                              ? '与卡片「现价」同一数据源（最新成交价）'
                                              : '保护单触发基准＝最新成交价（卡片「现价」显示的是标记价，极端行情下两者会有细微差异）'}>
                                        现价 {fmtPx(view.lastPx)}
                                    </span>
                                    <span className="dim"
                                          title={view.costFallback
                                              ? '手续费/资金费不可得，成本价回退为开仓均价'
                                              : '成本价 = 均价 + 开仓手续费 + 已结算资金费 + 预估平仓费'}>
                                        成本 {fmtPx(view.costPx)}
                                    </span>
                                    {view.progresses.map(d => (
                                        <span key={d.label}
                                              className={d.crossed ? 'warn' : (null == d.pct ? 'dim' : d.cls)}>
                                            {d.crossed ? `${d.label} 已越过` : (
                                                <>
                                                    {d.label}{' '}
                                                    {/* 进度为负＝现价尚在亏损侧：仅数值标红，标签沿用外层颜色 */}
                                                    <span className={null != d.pct && d.pct < 0 ? 'warn' : undefined}>
                                                        {null == d.pct ? '--'
                                                            : `${d.pct >= 0 ? '+' : ''}${d.pct.toFixed(1)}%`}
                                                    </span>
                                                </>
                                            )}
                                        </span>
                                    ))}
                                </div>
                            </>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

export default HedgeProtectRows;
